
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { UseFormReturn } from "react-hook-form";
import { OnboardingFormValues } from "../OnboardingFormSchema";
import { useWizard } from "../FormWizardContext";
import { StepIndicator } from "../StepIndicator";
import { StepDescription } from "../StepDescription";
import { BasicInfoStep } from "../steps/BasicInfoStep";
import { SocialMediaStep } from "../steps/SocialMediaStep";
import { RegionAndScopeStep } from "../steps/RegionAndScopeStep";
import { InvoiceGenerationStep } from "../steps/InvoiceGenerationStep";
import { ReviewStep } from "../steps/ReviewStep";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Lock } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface FormContentProps {
  form: UseFormReturn<OnboardingFormValues>;
  onSubmit: (values: OnboardingFormValues) => Promise<void>;
  isSubmitting?: boolean;
  currentPhase?: number;
  formId?: string;
}

export function FormContent({ 
  form, 
  onSubmit, 
  isSubmitting = false, 
  currentPhase = 1,
  formId
}: FormContentProps) {
  const { 
    currentStep, 
    nextStep, 
    previousStep, 
    totalSteps, 
    setDynamicTotalSteps,
    triggerStepValidation,
    setTriggerStepValidation,
    setCurrentStepDirect
  } = useWizard();
  
  const [isValidating, setIsValidating] = useState(false);
  const [stepTitles, setStepTitles] = useState<string[]>([]);
  const [disabledSteps, setDisabledSteps] = useState<number[]>([]);
  const isMobile = useIsMobile();
  
  // Effect to run validation when step changes via direct navigation
  useEffect(() => {
    if (triggerStepValidation) {
      const fieldsToValidate = getFieldsToValidateForStep(currentStep);
      form.trigger(fieldsToValidate as any).then(isValid => {
        if (isValid) {
          // Only change steps if validation passes
          setCurrentStepDirect(currentStep);
        }
        setTriggerStepValidation(false);
      });
    }
  }, [triggerStepValidation, currentStep, form, setCurrentStepDirect, setTriggerStepValidation]);
  
  // Configure steps based on current phase
  useEffect(() => {
    console.log("Current phase in FormContent:", currentPhase);
    if (currentPhase === 1) {
      // Phase 1: Creator Info, Content Info, Additional Info, Review
      setStepTitles(["Creator Information", "Content Information", "Additional Information", "Review"]);
      setDynamicTotalSteps(4);
      setDisabledSteps([]);
    } else if (currentPhase >= 3) {
      // Phase 3+: Invoice Generation, Review
      setStepTitles(["Invoice Generation", "Review"]);
      setDynamicTotalSteps(2);
      setDisabledSteps([]);
    } else {
      // Phase 2: Keep default steps but disable Invoice step
      setStepTitles(["Creator Info", "Content Info", "Additional Info", "Invoice", "Review"]);
      setDynamicTotalSteps(5);
      setDisabledSteps([3]); // Disable Invoice step (index 3)
    }
  }, [currentPhase, setDynamicTotalSteps]);
  
  // Determine if the invoice step should be available based on the current phase
  const isInvoiceStepAvailable = currentPhase >= 3;
  
  // For phase 1, we only want to show 4 steps
  const isPhase1 = currentPhase === 1;

  // Check if we're on the review step
  const isReviewStep = (
    (isPhase1 && currentStep === 4) || 
    (currentPhase >= 3 && currentStep === 2) || 
    (currentStep === totalSteps)
  );
  
  const handleNext = async () => {
    // Get fields to validate for the current step
    const fieldsToValidate = getFieldsToValidateForStep(currentStep);
    
    setIsValidating(true);
    try {
      const result = await form.trigger(fieldsToValidate as any);
      if (result) {
        nextStep();
      }
    } finally {
      setIsValidating(false);
    }
  };

  const getFieldsToValidateForStep = (step: number) => {
    // If we're in Phase 1 (4-step form)
    if (isPhase1) {
      // Phase 1 steps
      switch (step) {
        case 1: // Creator Information
          return ["creator_name", "email"];
        case 2: // Content Information
          return ["social_media_type", "social_media_handler", "social_media_name", "agreed_rate", "currency_type", "pic_id"];
        case 3: // Additional Information
          return ["region", "deliverables"];
        default:
          return [];
      }
    } else if (currentPhase >= 3) {
      // Phase 3+ steps
      switch (step) {
        case 1: // Invoice Generation
          return ["invoice_number", "invoice_date", "wallet_address", "wallet_address_network", "chain", "amount"]; 
        default:
          return [];
      }
    } else {
      // Default step validation (Phase 2)
      switch (step) {
        case 1:
          return ["creator_name", "email"];
        case 2:
          return ["social_media_type", "social_media_handler", "social_media_name", "agreed_rate", "currency_type", "pic_id"];
        case 3:
          return ["region", "deliverables"];
        case 4:
          return [];
        default:
          return [];
      }
    }
  };

  // Handle form submission with proper data consistency checks
  const handleSubmit = () => {
    console.log("Form submission triggered");
    
    // Log all form values before submission for debugging
    console.log("All form values before submission:", form.getValues());
    
    // Use explicit promise handling for better tracking
    form.handleSubmit((data) => {
      console.log("Form validated successfully, submitting data:", data);
      
      // Call the onSubmit function that was passed down as prop
      onSubmit(data)
        .then(() => {
          console.log("Form submission completed successfully");
        })
        .catch((error) => {
          console.error("Form submission failed:", error);
        });
    })();
  };

  const renderStep = () => {
    if (isPhase1) {
      // Phase 1 steps
      switch (currentStep) {
        case 1:
          return <BasicInfoStep form={form} />;
        case 2:
          return <SocialMediaStep form={form} />;
        case 3:
          return <RegionAndScopeStep form={form} />;
        case 4:
          return <ReviewStep 
                   form={form} 
                   onSubmit={onSubmit} 
                   formId={formId}
                   isSubmitting={isSubmitting} 
                 />;
        default:
          return null;
      }
    } else if (currentPhase >= 3) {
      // Phase 3+ steps - Corrected to ensure Invoice Generation shows first
      switch (currentStep) {
        case 1:
          return <InvoiceGenerationStep form={form} formId={formId} />;
        case 2:
          return <ReviewStep 
                   form={form} 
                   onSubmit={onSubmit} 
                   formId={formId}
                   isSubmitting={isSubmitting} 
                 />;
        default:
          return null;
      }
    } else {
      // Default steps (Phase 2)
      switch (currentStep) {
        case 1:
          return <BasicInfoStep form={form} />;
        case 2:
          return <SocialMediaStep form={form} />;
        case 3:
          return <RegionAndScopeStep form={form} />;
        case 4:
          return isInvoiceStepAvailable ? (
            <InvoiceGenerationStep form={form} formId={formId} />
          ) : (
            <div className="flex flex-col items-center justify-center p-10 space-y-4 text-center">
              <Lock className="h-16 w-16 text-gray-400" />
              <h3 className="text-xl font-medium">Invoice Generation Locked</h3>
              <p className="text-gray-500 max-w-md">
                Invoice generation will be available once your application has been reviewed and approved (Phase 3).
                You're currently in Phase {currentPhase}.
              </p>
            </div>
          );
        case 5:
          return <ReviewStep 
                   form={form} 
                   onSubmit={onSubmit} 
                   formId={formId}
                   isSubmitting={isSubmitting} 
                 />;
        default:
          return null;
      }
    }
  };

  // The ReviewStep component has its own submit button, so we only show navigation
  // buttons on non-review steps
  const showNavigationButtons = !isReviewStep;

  // Determine the correct button text based on phase and current step
  const getButtonText = () => {
    if (isValidating) {
      return "Validating...";
    }
    
    // For Phase 3, we need special handling
    if (currentPhase >= 3) {
      // Step 1 is Invoice Generation, Step 2 is Review
      if (currentStep === 1) {
        return "Next to Review";
      }
    }
    
    // For Phase 1, customize based on step
    if (isPhase1) {
      if (currentStep === 3) { // Last step before review in Phase 1
        return "Review Application";
      }
    }
    
    return "Next";
  }

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      <StepIndicator 
        stepTitles={stepTitles} 
        disabledSteps={disabledSteps}
      />
      
      <StepDescription />
      
      <div className="bg-gray-50 dark:bg-gray-800/50 p-4 md:p-6 rounded-lg shadow-inner border border-gray-100 dark:border-gray-700">
        {renderStep()}
      </div>
      
      {showNavigationButtons && (
        <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'justify-between'} pt-2`}>
          {isMobile ? (
            <>
              <Button
                variant="outline"
                onClick={previousStep}
                disabled={currentStep === 1 || isSubmitting || isValidating}
                className="border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 w-full"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Previous
              </Button>
              
              <Button 
                onClick={handleNext} 
                disabled={isValidating || isSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white w-full"
              >
                {getButtonText()} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={previousStep}
                disabled={currentStep === 1 || isSubmitting || isValidating}
                className="border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Previous
              </Button>
              
              <Button 
                onClick={handleNext} 
                disabled={isValidating || isSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {getButtonText()} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
